import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { 
    Box, 
    Button, 
    Stack, 
    IconButton, 
    Divider,
    Sheet
} from '@mui/joy'
import { TextB, TextItalic, TextUnderline, TextStrikethrough, ListBullets, ListNumbers, Quotes, Code, ImageSquare, LinkSimple, ArrowCounterClockwise, ArrowClockwise } from '@phosphor-icons/react'

const MenuBar = ({ editor, onImageUpload }) => {
    if (!editor) {
        return null
    }

    const addImage = () => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.onchange = async (event) => {
            const file = event.target.files[0]
            if (file) {
                onImageUpload(file)
            }
        }
        input.click()
    }

    const setLink = () => {
        const url = window.prompt('URL')
        if (url) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
        }
    }

    return (
        <Stack 
            direction="row" 
            spacing={0.5} 
            sx={{ 
                p: 1, 
                borderBottom: '1px solid', 
                borderColor: 'neutral.700',
                flexWrap: 'wrap'
            }}
        >
            <IconButton
                size="sm"
                variant={editor.isActive('bold') ? 'soft' : 'outlined'}
                color={editor.isActive('bold') ? 'primary' : 'neutral'}
                onClick={() => editor.chain().focus().toggleBold().run()}
            >
                <TextB size={18} />
            </IconButton>

            <IconButton
                size="sm"
                variant={editor.isActive('italic') ? 'soft' : 'outlined'}
                color={editor.isActive('italic') ? 'primary' : 'neutral'}
                onClick={() => editor.chain().focus().toggleItalic().run()}
            >
                <TextItalic size={18} />
            </IconButton>

            <IconButton
                size="sm"
                variant={editor.isActive('underline') ? 'soft' : 'outlined'}
                color={editor.isActive('underline') ? 'primary' : 'neutral'}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
                <TextUnderline size={18} />
            </IconButton>

            <IconButton
                size="sm"
                variant={editor.isActive('strike') ? 'soft' : 'outlined'}
                color={editor.isActive('strike') ? 'primary' : 'neutral'}
                onClick={() => editor.chain().focus().toggleStrike().run()}
            >
                <TextStrikethrough size={18} />
            </IconButton>

            <Divider orientation="vertical" />

            <IconButton
                size="sm"
                variant={editor.isActive('bulletList') ? 'soft' : 'outlined'}
                color={editor.isActive('bulletList') ? 'primary' : 'neutral'}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
                <ListBullets size={18} />
            </IconButton>

            <IconButton
                size="sm"
                variant={editor.isActive('orderedList') ? 'soft' : 'outlined'}
                color={editor.isActive('orderedList') ? 'primary' : 'neutral'}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
                <ListNumbers size={18} />
            </IconButton>

            <IconButton
                size="sm"
                variant={editor.isActive('blockquote') ? 'soft' : 'outlined'}
                color={editor.isActive('blockquote') ? 'primary' : 'neutral'}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
            >
                <Quotes size={18} />
            </IconButton>

            <IconButton
                size="sm"
                variant={editor.isActive('codeBlock') ? 'soft' : 'outlined'}
                color={editor.isActive('codeBlock') ? 'primary' : 'neutral'}
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            >
                <Code size={18} />
            </IconButton>

            <Divider orientation="vertical" />

            <IconButton
                size="sm"
                variant="outlined"
                color="neutral"
                onClick={setLink}
            >
                <LinkSimple size={18} />
            </IconButton>

            <IconButton
                size="sm"
                variant="outlined"
                color="neutral"
                onClick={addImage}
            >
                <ImageSquare size={18} />
            </IconButton>

            <Divider orientation="vertical" />

            <IconButton
                size="sm"
                variant="outlined"
                color="neutral"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
            >
                <ArrowCounterClockwise size={18} />
            </IconButton>

            <IconButton
                size="sm"
                variant="outlined"
                color="neutral"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
            >
                <ArrowClockwise size={18} />
            </IconButton>
        </Stack>
    )
}

const RichTextEditor = ({ value, onChange, placeholder, onImageUpload, ...props }) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Image.configure({
                HTMLAttributes: {
                    class: 'rich-text-image',
                },
            }),
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML())
        },
        editorProps: {
            attributes: {
                placeholder: placeholder || 'Начните вводить текст...',
            },
        },
    })

    const handleImageUpload = async (file) => {
        if (onImageUpload) {
            try {
                const imageUrl = await onImageUpload(file)
                if (imageUrl && editor) {
                    editor.chain().focus().setImage({ src: imageUrl }).run()
                }
            } catch (error) {
                console.error('Error uploading image:', error)
            }
        }
    }

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'neutral.700',
                borderRadius: '16px',
                overflow: 'hidden',
                '& .ProseMirror': {
                    p: 2,
                    minHeight: '200px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    '&:focus': {
                        outline: 'none'
                    },
                    '& ul, & ol': {
                        pl: 2
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                        mt: 1,
                        mb: 0.5,
                        fontWeight: 'bold'
                    },
                    '& blockquote': {
                        borderLeft: '4px solid',
                        borderColor: 'primary.700',
                        pl: 2,
                        ml: 0,
                        fontStyle: 'italic',
                        color: 'text.secondary'
                    },
                    '& .rich-text-image': {
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '4px'
                    },
                    '& code': {
                        backgroundColor: 'neutral.100',
                        px: 0.5,
                        borderRadius: '2px',
                        fontFamily: 'monospace'
                    },
                    '& pre': {
                        backgroundColor: 'neutral.100',
                        p: 1,
                        borderRadius: '4px',
                        overflow: 'auto'
                    }
                },
                ...props.sx
            }}
        >
            <MenuBar editor={editor} onImageUpload={handleImageUpload} />
            <EditorContent editor={editor} />
        </Box>
    )
}

export default RichTextEditor